import * as React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useNotification } from "../../context/notification-provider";
import MappingForm, {
  FormFieldValue,
  MappingFormState,
  MappingFormValues,
  validateMapping,
} from "./mapping-form";
import { createMapping } from "./service";
import useMdaSourceColumns from "./use-mda-source-columns";

const MappingCreateDialog = ({
  clientId,
  nextColumnPosition,
  open,
  onClose,
}: {
  clientId: number;
  nextColumnPosition: number;
  open: boolean;
  onClose: () => void;
}) => {
  const { showNotification } = useNotification();
  const { data: sourceColumns } = useMdaSourceColumns();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [formState, setFormState] = React.useState<MappingFormState>({
    values: { enabled: true },
    errors: {},
  });
  const formValues = formState.values;

  const handleFieldChange = React.useCallback(
    (name: keyof MappingFormValues, value: FormFieldValue) => {
      setFormState((previousState) => ({
        ...previousState,
        values: { ...previousState.values, [name]: value },
      }));
    },
    [],
  );

  const handleSubmit = React.useCallback(async () => {
    const { issues } = validateMapping(formValues);

    if (issues.length > 0) {
      setFormState((previousState) => ({
        ...previousState,
        errors: Object.fromEntries(
          issues.map((issue) => [issue.path[0], issue.message]),
        ),
      }));
      return;
    }

    setFormState((previousState) => ({ ...previousState, errors: {} }));
    setIsSubmitting(true);

    try {
      await createMapping({
        clientId,
        sourceColumn: formValues.sourceColumn ?? "",
        destinationColumn: formValues.destinationColumn ?? null,
        columnPosition: nextColumnPosition,
        enabled: formValues.enabled ?? true,
      });

      showNotification({
        message: "Column mapping created successfully.",
        severity: "success",
        duration: 3000,
      });

      setFormState({ values: { enabled: true }, errors: {} });
      onClose();
    } catch (createError) {
      showNotification({
        message: `Failed to create column mapping. Reason: ${(createError as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [formValues, clientId, nextColumnPosition, showNotification, onClose]);

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ borderBottom: 2, mb: 2, borderColor: "divider" }}>
        Add Column Mapping
      </DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <MappingForm
          formState={formState}
          sourceColumns={sourceColumns ?? []}
          onFieldChange={handleFieldChange}
          onSubmit={handleSubmit}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          Add mapping
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MappingCreateDialog;
